import React, {type ReactNode} from 'react';

import moment from 'moment-tz';

import {ClickToEditButton} from 'app/components/general/ClickToEditButton';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {DocumentMetadataStatusButtons} from 'app/general/structures/DocumentMetadataStatusButtons';
import MetaContentTypeName from 'app/general/structures/MetaContentTypeName';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {ModifiedCreatedDataStamp} from 'app/procedures/components/ViewProcedureMetaData/ModifiedCreatedDataStamp';
import {DocumentTagsList} from 'app/tags/components/DocumentTagsList';
import {getQuizName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {RecommendedDocuments} from './QuizEditPage/RecommendedDocument';

import type {QuizContentType} from 'app/quizzes/types';
import type {QuizSerializedType} from 'sweet-types';

type Props = {
  advancedModeEnabled?: boolean;
  warningComponent?: ReactNode;
  quiz: QuizSerializedType;
  takeQuiz?: () => unknown;
  content: QuizContentType;
  editableEmptyDataMessage?: string;
  editMode?: boolean;
};

export const ViewQuizMetadata = ({
  advancedModeEnabled,
  warningComponent,
  quiz,
  takeQuiz,
  content,
  editableEmptyDataMessage,
  editMode,
}: Props) => {
  const hasNoTags = quiz.tags.length === 0;

  return (
    <DocHeaderStructure
      warningContent={warningComponent}
      advancedContent={
        <>
          {advancedModeEnabled === true ? (
            <div className="row">
              <div className="col">
                <ModifiedCreatedDataStamp document={quiz} />
              </div>
            </div>
          ) : null}
          <MetaContentTypeName
            contentType="quiz"
            statusContent={
              <DocumentMetadataStatusButtons
                canEdit={quiz.can_edit}
                contentType="quiz"
                context={'view'}
                document={quiz}
              />
            }
          />
        </>
      }
      tagsContent={
        <>
          {content.description ? (
            <div className="mb-3">
              <UserContentContainer
                id="doc-description"
                content={content.description}
              />
            </div>
          ) : null}
          {hasNoTags && editableEmptyDataMessage != null ? (
            <div className="form-group">
              <span className="small text-secondary font-weight-light">
                {editableEmptyDataMessage}
              </span>
            </div>
          ) : (
            <DocumentTagsList tags={quiz.tags} />
          )}
          {content.duration ? (
            <p className="mb-1">
              {gt(
                'Duration: %s',
                moment.duration(content.duration, 'seconds').humanize()
              )}
            </p>
          ) : null}
          {content.pass_mark ? (
            <p className="mb-1">{gt('Pass Mark: %s', content.pass_mark)}%</p>
          ) : null}
          <RecommendedDocuments quiz={quiz} />
          {takeQuiz && (
            <button onClick={takeQuiz} className="btn btn-primary mt-4">
              <i className="fa fa-play mr-1" /> {gt('Take this quiz')}
            </button>
          )}
        </>
      }
      titleContent={
        <h1
          className={`fr-view mb-4 ${
            isRightToLeftWrapper(quiz.name) ? 'right-to-left-text' : ''
          }`}
        >
          {typeof content.name === 'string'
            ? getQuizName(content, true)
            : content.name}
          {editMode ? <ClickToEditButton /> : null}
        </h1>
      }
    />
  );
};
